import { supabase } from './supabaseClient';
import type {
  MicroAction,
  UserBadge,
  Badge,
  CivicLevel,
  XPAwardResult
} from './civicLevelTypes';

export const LEVEL_THRESHOLDS: Record<CivicLevel, { min: number; max: number }> = {
  'Observer': { min: 0, max: 499 },
  'Explorer': { min: 500, max: 1499 },
  'Sense-Maker': { min: 1500, max: 3999 },
  'Advocate': { min: 4000, max: 9999 },
  'Catalyst': { min: 10000, max: 24999 },
  'Change-Maker': { min: 25000, max: Infinity },
};

export const LEVEL_ORDER: CivicLevel[] = [
  'Observer',
  'Explorer',
  'Sense-Maker',
  'Advocate',
  'Catalyst',
  'Change-Maker',
];

export async function getAllMicroActions(): Promise<MicroAction[]> {
  const { data, error } = await supabase
    .from('micro_actions')
    .select('*')
    .order('tier', { ascending: true })
    .order('xp_value', { ascending: true });

  if (error) {
    console.error('Error fetching micro actions:', error);
    return [];
  }

  return data || [];
}

export async function getMicroActionByTitle(title: string): Promise<MicroAction | null> {
  const { data, error } = await supabase
    .from('micro_actions')
    .select('*')
    .eq('title', title)
    .maybeSingle();

  if (error) {
    console.error('Error fetching micro action by title:', error);
    return null;
  }

  return data;
}

export async function getAvailableMicroActions(
  currentLevel: CivicLevel
): Promise<MicroAction[]> {
  const allActions = await getAllMicroActions();
  const levelIndex = LEVEL_ORDER.indexOf(currentLevel);

  return allActions.filter(action => {
    if (!action.is_locked_until_level) return true;
    const requiredLevelIndex = LEVEL_ORDER.indexOf(action.is_locked_until_level as CivicLevel);
    return levelIndex >= requiredLevelIndex;
  });
}

export async function awardXP(
  userId: string,
  actionId: string
): Promise<XPAwardResult | null> {
  try {
    const { data, error } = await supabase.rpc('award_xp_and_update_level', {
      p_user_id: userId,
      p_action_id: actionId,
    });

    if (error) {
      console.error('Error awarding XP:', error);
      return null;
    }

    return data as XPAwardResult;
  } catch (err) {
    console.error('Exception awarding XP:', err);
    return null;
  }
}

export async function getUserBadges(userId: string): Promise<Badge[]> {
  const { data, error } = await supabase
    .from('user_badges')
    .select(`
      awarded_at,
      badges (
        id,
        level_name,
        icon_symbol,
        color_primary,
        color_secondary,
        description,
        level_number,
        xp_threshold
      )
    `)
    .eq('user_id', userId)
    .order('awarded_at', { ascending: false });

  if (error) {
    console.error('Error fetching user badges:', error);
    return [];
  }

  return (data || [])
    .map((ub: any) => ub.badges)
    .filter(Boolean);
}

export async function getAllBadges(): Promise<Badge[]> {
  const { data, error } = await supabase
    .from('badges')
    .select('*')
    .order('level_number', { ascending: true });

  if (error) {
    console.error('Error fetching badges:', error);
    return [];
  }

  return data || [];
}

export function calculateLevelProgress(
  currentXP: number,
  currentLevel: CivicLevel
): { percent: number; current: number; needed: number } {
  const threshold = LEVEL_THRESHOLDS[currentLevel];
  const levelIndex = LEVEL_ORDER.indexOf(currentLevel);

  if (levelIndex === LEVEL_ORDER.length - 1) {
    return { percent: 100, current: currentXP, needed: currentXP };
  }

  const nextLevel = LEVEL_ORDER[levelIndex + 1];
  const nextThreshold = LEVEL_THRESHOLDS[nextLevel];

  const xpInCurrentLevel = currentXP - threshold.min;
  const xpNeededForNextLevel = nextThreshold.min - threshold.min;
  const percent = Math.min(100, (xpInCurrentLevel / xpNeededForNextLevel) * 100);

  return {
    percent,
    current: xpInCurrentLevel,
    needed: xpNeededForNextLevel,
  };
}

export function getNextLevel(currentLevel: CivicLevel): CivicLevel | null {
  const levelIndex = LEVEL_ORDER.indexOf(currentLevel);
  if (levelIndex === LEVEL_ORDER.length - 1) return null;
  return LEVEL_ORDER[levelIndex + 1];
}

export function getLevelDescription(level: CivicLevel): string {
  const descriptions: Record<CivicLevel, string> = {
    'Observer': "You're beginning to tune into the world around you. Showing up is the first step.",
    'Explorer': "Your sustained curiosity is revealing patterns. Keep returning, keep noticing.",
    'Sense-Maker': "Through reflection over time, you're understanding how things connect.",
    'Advocate': "Your voice carries the weight of practice. You speak from what you've learned.",
    'Catalyst': "Your steady presence is creating ripples. Change happens through consistency.",
    'Change-Maker': "Your long-term commitment shapes real outcomes. This is civic practice.",
  };

  return descriptions[level];
}

export function getTierName(tier: number): string {
  const tierNames: Record<number, string> = {
    1: 'Awareness',
    2: 'Sense-making',
    3: 'Advocacy',
    4: 'Catalyst',
    5: 'Change-maker',
  };
  return tierNames[tier] || 'Unknown';
}

export async function getRecentXPActivity(
  userId: string,
  limit: number = 10
): Promise<Array<{ action: MicroAction; xp: number; date: string }>> {
  const { data, error } = await supabase
    .from('user_micro_actions_log')
    .select(`
      xp_awarded,
      created_at,
      micro_actions (
        id,
        title,
        tier,
        xp_value,
        description
      )
    `)
    .eq('user_id', userId)
    .order('created_at', { ascending: false })
    .limit(limit);

  if (error) {
    console.error('Error fetching XP activity:', error);
    return [];
  }

  return (data || []).map((log: any) => ({
    action: log.micro_actions,
    xp: log.xp_awarded,
    date: log.created_at,
  }));
}

export async function trackPassiveEngagement(
  userId: string,
  engagementType: 'story_opened' | 'timeline_viewed' | 'backstory_read' | 'item_saved',
  baseCP: number = 1,
  storyId?: string
): Promise<{
  cpAwarded: number;
  isCapped: boolean;
  newTotalXP?: number;
  oldLevel?: string;
  newLevel?: string;
  leveledUp?: boolean;
  xpProgress?: number;
} | null> {
  try {
    console.log('[trackPassiveEngagement] Calling RPC with:', {
      userId,
      engagementType,
      baseCP,
      storyId
    });

    const { data, error } = await supabase.rpc('track_passive_engagement', {
      p_user_id: userId,
      p_engagement_type: engagementType,
      p_base_mp: baseCP,
      p_story_id: storyId || null,
    });

    console.log('[trackPassiveEngagement] RPC response:', { data, error });

    if (error) {
      console.error('[trackPassiveEngagement] Error from database:', error);
      return null;
    }

    if (!data) {
      console.error('[trackPassiveEngagement] No data returned from database');
      return null;
    }

    const result = {
      cpAwarded: data.cp_awarded || 0,
      isCapped: data.is_capped || false,
      newTotalXP: data.new_total_xp,
      oldLevel: data.old_level,
      newLevel: data.new_level,
      leveledUp: data.leveled_up || false,
      xpProgress: data.xp_progress,
    };

    console.log('[trackPassiveEngagement] Returning:', result);

    return result;
  } catch (err) {
    console.error('[trackPassiveEngagement] Exception:', err);
    return null;
  }
}

export async function trackActiveEngagement(
  userId: string,
  storyId: string,
  interactionType: 'listen_now_clicked' | 'emotion_clicked' | 'quick_fact' | 'look_deeper' | 'explore_connections' | 'take_it_further' | 'save_story'
): Promise<{
  cpAwarded: number;
  isCapped: boolean;
  newTotalXP?: number;
  oldLevel?: string;
  newLevel?: string;
  leveledUp?: boolean;
  xpProgress?: number;
  reason?: string;
} | null> {
  console.log('=== [trackActiveEngagement] START ===');
  console.log('[trackActiveEngagement] User ID:', userId);
  console.log('[trackActiveEngagement] Story ID:', storyId);
  console.log('[trackActiveEngagement] Interaction Type:', interactionType);

  try {
    console.log('[trackActiveEngagement] Calling Supabase RPC: track_active_engagement');

    const { data, error } = await supabase.rpc('track_active_engagement', {
      p_user_id: userId,
      p_story_id: storyId,
      p_interaction_type: interactionType,
    });

    console.log('[trackActiveEngagement] RPC Raw Response:', { data, error });

    if (error) {
      console.error('[trackActiveEngagement] ❌ Database Error:', error);
      return null;
    }

    if (!data) {
      console.error('[trackActiveEngagement] ❌ No data returned from database');
      return null;
    }

    const result = {
      cpAwarded: data.cp_awarded || 0,
      isCapped: data.is_capped || false,
      newTotalXP: data.new_total_xp,
      oldLevel: data.old_level,
      newLevel: data.new_level,
      leveledUp: data.leveled_up || false,
      xpProgress: data.xp_progress,
      reason: data.reason,
    };

    console.log('[trackActiveEngagement] ✅ CP Awarded:', result.cpAwarded);
    console.log('[trackActiveEngagement] ✅ New Total XP:', result.newTotalXP);
    console.log('[trackActiveEngagement] ✅ Is Capped:', result.isCapped);
    console.log('[trackActiveEngagement] ✅ Reason:', result.reason);
    console.log('[trackActiveEngagement] Full Result:', result);
    console.log('=== [trackActiveEngagement] SUCCESS ===');

    return result;
  } catch (err) {
    console.error('=== [trackActiveEngagement] EXCEPTION ===', err);
    return null;
  }
}

export async function getDailyCPProgress(userId: string): Promise<{
  activeCP: number;
  passiveCP: number;
  activeCap: number;
  passiveCap: number;
} | null> {
  try {
    const today = new Date().toISOString().split('T')[0];

    const { data, error } = await supabase
      .from('daily_mp_earned')
      .select('active_cp, passive_cp')
      .eq('user_id', userId)
      .eq('date', today)
      .maybeSingle();

    if (error) {
      console.error('Error fetching daily CP:', error);
      return null;
    }

    return {
      activeCP: data?.active_cp || 0,
      passiveCP: data?.passive_cp || 0,
      activeCap: 90,
      passiveCap: 5,
    };
  } catch (err) {
    console.error('Exception fetching daily CP:', err);
    return null;
  }
}
